import { CommonModule } from '@angular/common';
import {
  ChangeDetectionStrategy,
  Component,
  computed,
  DestroyRef,
  inject,
  input,
  linkedSignal,
  OnInit,
  output,
  signal,
} from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { MatButtonModule } from '@angular/material/button';
import { MatIconModule } from '@angular/material/icon';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { MatTooltipModule } from '@angular/material/tooltip';
import { GouvGeorisquesDto, PropertyDto, UserApplications } from '@workspace/data-types';
import { ApplicationStore, UserStore } from '@workspace/ng-data-access';
import { ApiService } from '@workspace/ng-http';
import { catchError, EMPTY, finalize, of, tap } from 'rxjs';
import { PropertyGeohazardsCardComponent } from '../cards/geohazards-card/geohazards-card.component';
import { PropertyInseeCardComponent } from '../cards/insee-card/insee-card.component';
import { PropertyNoiseCardComponent } from '../cards/noise-card/noise-card.component';
import { PropertySitadelCardComponent } from '../cards/sitadel-card/sitadel-card.component';

@Component({
  selector: 'etxe-property-geospatial-display',
  imports: [
    CommonModule,
    MatIconModule,
    MatButtonModule,
    MatProgressSpinnerModule,
    MatTooltipModule,
    PropertyGeohazardsCardComponent,
    PropertyInseeCardComponent,
    PropertyNoiseCardComponent,
    PropertySitadelCardComponent,
  ],
  templateUrl: './geospatial-display.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class PropertyGeospatialDisplayComponent implements OnInit {
  private readonly destroyRef = inject(DestroyRef);
  private apiService = inject(ApiService);
  private readonly appStore = inject(ApplicationStore);
  private readonly userStore = inject(UserStore);

  readonly isAdminApp = computed(() => this.appStore.name() === UserApplications.ADMIN);

  property = input.required<PropertyDto>();
  fetchData = input<boolean>(false);
  showHero = input<boolean>(true);
  showRefreshButton = input<boolean>(true);
  collapsible = input<boolean>(false);
  isOwner = input<boolean>(true);
  propertyRefreshed = output<PropertyDto>();

  isMainExpanded = linkedSignal(() => !this.collapsible());

  private fetchedProperty = signal<PropertyDto | null>(null);
  private fallbackGeohazards = signal<GouvGeorisquesDto | null>(null);

  isLoading = signal<boolean>(false);
  error = signal<string | null>(null);

  geohazardsExpanded = signal<boolean>(false);
  inseeExpanded = signal<boolean>(false);
  noiseExpanded = signal<boolean>(false);
  sitadelExpanded = signal<boolean>(false);

  allExpanded = computed(
    () => this.geohazardsExpanded() && this.inseeExpanded() && this.noiseExpanded() && this.sitadelExpanded(),
  );

  currentProperty = computed(() => this.fetchedProperty() ?? this.property());
  geospatial = computed(() => this.currentProperty()?.geospatial);

  geohazard = computed(() => this.geospatial()?.geohazard ?? this.fallbackGeohazards());
  insee = computed(() => this.geospatial()?.insee);
  noise = computed(() => this.geospatial()?.noise);
  sitadel = computed(() => this.geospatial()?.sitadel);

  addressLabel = computed(() => this.currentProperty()?.location?.address?.label);
  cityName = computed(() => this.currentProperty()?.location?.address?.city);
  cityCode = computed(() => this.currentProperty()?.location?.address?.cityCode);
  coordinates = computed(() => this.currentProperty()?.location?.coordinates);

  risksSummary = computed(() => {
    const geo = this.geohazard();
    if (!geo) return null;
    let count = 0;
    if (geo.risquesNaturels) {
      Object.values(geo.risquesNaturels).forEach((r) => {
        if (r && typeof r === 'object' && 'present' in r && r.present) count++;
      });
    }
    if (geo.risquesTechnologiques) {
      Object.values(geo.risquesTechnologiques).forEach((r) => {
        if (r && typeof r === 'object' && 'present' in r && r.present) count++;
      });
    }
    return { count, isSafe: count === 0 };
  });

  ngOnInit() {
    if (this.fetchData()) {
      const geo = this.geospatial();
      const needsRefresh = !geo || !geo.geohazard || !geo.insee || !geo.noise || !geo.sitadel;

      if (needsRefresh) {
        this.fetchGeospatialData();
      }
    }
  }

  fetchGeospatialData() {
    const prop = this.property();
    const propertyId = prop?._id;
    if (!propertyId) return;

    const loc = prop.location;
    const latlon =
      loc?.coordinates?.latitude && loc?.coordinates?.longitude
        ? `${loc.coordinates.longitude},${loc.coordinates.latitude}`
        : undefined;

    this.isLoading.set(true);
    this.error.set(null);

    const geoData$ = prop?.geospatial?.geohazard
      ? of(prop.geospatial.geohazard)
      : latlon
        ? this.apiService.business().etxe().public().gouv().georisques(latlon)
        : of(null);

    geoData$
      .pipe(
        tap((geoData) => {
          if (geoData) {
            this.fallbackGeohazards.set(geoData);
            const current = this.property();
            if (current) {
              const updatedGeo = {
                ...(current.geospatial || {}),
                geohazard: geoData,
              };
              const updatedProp = {
                ...current,
                geospatial: updatedGeo,
              };
              this.propertyRefreshed.emit(updatedProp);
            }
          }
        }),
        catchError((err) => {
          console.error('Error fetching geospatial data:', err);
          this.error.set('Impossible de charger l’intégralité des données géospatiales.');
          return EMPTY;
        }),
        finalize(() => this.isLoading.set(false)),
        takeUntilDestroyed(this.destroyRef),
      )
      .subscribe();
  }

  toggleAllSections() {
    const next = !this.allExpanded();
    this.geohazardsExpanded.set(next);
    this.inseeExpanded.set(next);
    this.noiseExpanded.set(next);
    this.sitadelExpanded.set(next);
  }

  scrollToSection(sectionId: string) {
    if (this.collapsible() && !this.isMainExpanded()) {
      this.isMainExpanded.set(true);
    }
    if (sectionId === 'section-geohazards') this.geohazardsExpanded.set(true);
    if (sectionId === 'section-insee') this.inseeExpanded.set(true);
    if (sectionId === 'section-noise') this.noiseExpanded.set(true);
    if (sectionId === 'section-sitadel') this.sitadelExpanded.set(true);

    setTimeout(() => {
      const element = document.getElementById(sectionId);
      if (element) {
        element.scrollIntoView({ behavior: 'smooth', block: 'start' });
      }
    }, 100);
  }
}
